import React from "react";
import { toast } from "react-toastify";
import { useDeleteYoutubeConfigurationMutation, useDeleteVectorConfigurationMutation, useDeleteAiConfigurationMutation } from "../../../Services/instagram/Instagram";

interface DeleteConfigurationModalProps {
  closeModal: () => void;
  fieldsData: any;
  onSuccess?: () => void;
  refetchYoutubeData?: () => void;
  refetchVectorData?: () => void;
  refetchAiData?: () => void;
}

const DeleteConfigurationModal: React.FC<DeleteConfigurationModalProps> = ({
  closeModal,
  fieldsData,
  onSuccess,
  refetchYoutubeData,
  refetchVectorData,
  refetchAiData,
}) => {
  const [deleteYoutubeConfig, { isLoading: isDeletingYoutube }] = useDeleteYoutubeConfigurationMutation();
  const [deleteVectorConfig, { isLoading: isDeletingVector }] = useDeleteVectorConfigurationMutation();
  const [deleteAiConfig, { isLoading: isDeletingAi }] = useDeleteAiConfigurationMutation();
  
  const isLoading = isDeletingYoutube || isDeletingVector || isDeletingAi;

  const handleDelete = async () => {
    if (!fieldsData?.id) {
      toast.error("Invalid configuration ID");
      return;
    }

    try {
      // Delete YouTube configuration if it exists
      if (fieldsData.id) {
        const result = await deleteYoutubeConfig({ id: fieldsData.id });
        if ('error' in result) {
          throw new Error('Failed to delete YouTube configuration');
        }
      }

      // Delete Vector configuration if it exists
      if (fieldsData.vector_config?.id) {
        const resultVector = await deleteVectorConfig({ id: fieldsData.vector_config.id });
        if ('error' in resultVector) {
          throw new Error('Failed to delete Vector configuration');
        }
      }

      // Delete AI configuration if it exists
      if (fieldsData.ai_config?.id) {
        const resultAi = await deleteAiConfig({ id: fieldsData.ai_config.id });
        if ('error' in resultAi) {
          throw new Error('Failed to delete AI configuration');
        }
      }

      // Only show success if at least one configuration was deleted
      if (fieldsData.id || fieldsData.vector_config?.id || fieldsData.ai_config?.id) {
        toast.success("Configuration(s) deleted successfully");
        onSuccess?.();
        refetchYoutubeData?.();
        refetchVectorData?.();
        refetchAiData?.();
      } else {
        toast.info("No configurations to delete");
      }
      
      closeModal();
    } catch (error) {
      console.error("Error deleting configuration(s):", error);
      toast.error(error.message || "Failed to delete configuration(s). Please try again.");
    }
  };

  return (
    <div className="card-body">
      <h6>Are you sure you want to delete this configuration?</h6>
      <div className="d-flex justify-content-end mt-10">
        <button
          type="button"
          className="btn btn-secondary me-2"
          onClick={closeModal}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="button"
          className="btn btn-danger"
          onClick={handleDelete}
          disabled={isLoading}
        >
          {isLoading ? 'Deleting...' : 'Delete'}
        </button>
      </div>
    </div>
  );
};

export default DeleteConfigurationModal;
